import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getVocabularyTermChildren,
  type VocabTerm,
  type VocabularyDisplayDto,
} from '@/api/vocabularies';
import { termTitle } from '@/components/vocabulary/term-display';
import {
  TermInfoInline,
  TermInfoToggle,
} from '@/components/vocabulary/TermInfoInline';
import { cn } from '@/lib/utils';

import { Branch, CHEVRON_COL_PX, CHILDREN_STALE_MS, INDENT_PX } from './HpoTree';
import { TermAddAction } from './TermAddAction';

interface TreeRowProps {
  readonly node: VocabTerm;
  readonly depth: number;
  readonly display: VocabularyDisplayDto | null;
  readonly excludedHpoIds: ReadonlySet<string>;
  readonly onAddTerm: (term: { id: string; label: string }) => void;
}

/**
 * One row in the HPO browse tree. Fires a lookahead `/terms/{id}/children`
 * query so leaf rows can swap the chevron for a spacer — preventing the
 * user from ever reaching a "no narrower terms" dead end (PTV2-1167). The
 * lookahead shares its cache key with the `Branch` query, so the real
 * expand of a non-leaf node reuses the cached children without a second
 * round-trip.
 */
export function TreeRow({
  node,
  depth,
  display,
  excludedHpoIds,
  onAddTerm,
}: TreeRowProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const isExcluded = excludedHpoIds.has(node.id);
  const title = termTitle(node, display);
  const rowPadding = `${String(12 + depth * INDENT_PX)}px`;

  const childrenQuery = useQuery({
    queryKey: ['vocabulary', 'hpo', 'children', node.id],
    queryFn: ({ signal }) => getVocabularyTermChildren('hpo', node.id, signal),
    staleTime: CHILDREN_STALE_MS,
  });
  const isLeaf = childrenQuery.data?.length === 0;

  return (
    <div className={cn(depth === 0 && 'border-b border-border/40 last:border-b-0')}>
      <div
        className={cn(
          'flex items-center gap-1.5 py-1.5 pr-2 text-[13px] transition-colors',
          !isExcluded && 'hover:bg-accent/50',
          isExcluded && 'bg-muted/40',
        )}
        style={{ paddingLeft: rowPadding }}
      >
        {isLeaf ? (
          <span
            className="shrink-0"
            style={{ width: `${String(CHEVRON_COL_PX)}px` }}
            aria-hidden="true"
          />
        ) : (
          <ExpandButton
            open={open}
            onToggle={() => {
              setOpen((v) => !v);
            }}
          />
        )}
        <span className="min-w-0 flex-1 flex items-center gap-2">
          <span
            className={cn(
              'truncate',
              depth === 0 && 'font-medium',
              depth >= 2 && 'text-foreground/80',
              isExcluded ? 'text-muted-foreground' : 'text-foreground',
            )}
            title={title}
          >
            {title}
          </span>
          <span className="shrink-0 text-[11px] font-mono text-muted-foreground">{node.id}</span>
        </span>
        <TermInfoToggle
          open={infoOpen}
          onToggle={() => {
            setInfoOpen((v) => !v);
          }}
          termLabel={title}
        />
        <TermAddAction
          termId={node.id}
          label={title}
          excluded={isExcluded}
          onAddTerm={onAddTerm}
        />
      </div>
      {infoOpen && (
        <div className="pr-2 pb-1.5" style={{ paddingLeft: rowPadding }}>
          <TermInfoInline vocabularyId="hpo" termId={node.id} />
        </div>
      )}
      {open && (
        <Branch
          parentId={node.id}
          depth={depth + 1}
          display={display}
          excludedHpoIds={excludedHpoIds}
          onAddTerm={onAddTerm}
        />
      )}
    </div>
  );
}

interface ExpandButtonProps {
  readonly open: boolean;
  readonly onToggle: () => void;
}

function ExpandButton({ open, onToggle }: ExpandButtonProps): ReactElement {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={
        open
          ? t('patient.phenotypes.browse.collapse')
          : t('patient.phenotypes.browse.expand')
      }
      aria-expanded={open}
      className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
    >
      {open ? (
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}
